import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PROCESS } from '../constants';
import { SectionHead } from './Services';

export default function Process() {
  const pinRef = useRef(null);
  const barRef = useRef(null);
  const barMRef = useRef(null);
  const listRef = useRef(null);
  const dotsRef = useRef([]);
  const stepsRef = useRef([]);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const n = PROCESS.length;

    // p: 0..1 progress along the track; a step lights up once the bar reaches it
    const render = (p) => {
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
      if (barMRef.current) barMRef.current.style.transform = `scaleY(${p})`;
      PROCESS.forEach((_, i) => {
        const on = p >= (i + 0.5) / n - 0.001;
        dotsRef.current[i]?.classList.toggle('is-on', on);
        stepsRef.current[i]?.classList.toggle('is-on', on);
      });
    };
    render(1);

    const mm = gsap.matchMedia();
    mm.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
      render(0);
      const st = ScrollTrigger.create({
        trigger: pinRef.current,
        start: 'top top+=72',
        end: '+=120%',
        pin: true,
        scrub: 0.6,
        onUpdate: (self) => render(self.progress),
      });
      return () => {
        st.kill();
        render(1);
      };
    });
    // phones and tablets: no pinning, the line fills and steps light up as you scroll past
    mm.add('(max-width: 1023px) and (prefers-reduced-motion: no-preference)', () => {
      render(0);
      const st = ScrollTrigger.create({
        trigger: listRef.current,
        start: 'top 80%',
        end: 'bottom 55%',
        scrub: 0.4,
        onUpdate: (self) => render(self.progress),
      });
      return () => {
        st.kill();
        render(1);
      };
    });
    return () => mm.revert();
  }, []);

  return (
    <section id="process" aria-labelledby="process-title" className="relative">
      <div ref={pinRef} className="frame flex min-h-[calc(100vh-72px)] flex-col justify-center py-24 lg:py-12">
        <SectionHead id="process-title" title="How we work">
Four steps from problem to launch.
        </SectionHead>

        {/* progress track, desktop */}
        <div className="relative mt-16 hidden h-4 lg:block" aria-hidden="true">
          <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-line" />
          <div
            ref={barRef}
            className="absolute inset-x-0 top-1/2 h-[2px] origin-left -translate-y-1/2 rounded-full bg-accent"
            style={{ transform: 'scaleX(1)' }}
          />
          {PROCESS.map((_, i) => (
            <span
              key={i}
              ref={(el) => (dotsRef.current[i] = el)}
              className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-line bg-base transition-colors duration-300 [&.is-on]:border-accent [&.is-on]:bg-accent"
              style={{ left: `${((i + 0.5) / PROCESS.length) * 100}%` }}
            />
          ))}
        </div>

        <div ref={listRef} className="relative mt-14 pl-6 lg:mt-8 lg:pl-0">
        {/* progress line, phones and tablets */}
        <span aria-hidden="true" className="absolute bottom-0 left-[3px] top-0 w-px bg-line lg:hidden" />
        <span ref={barMRef} aria-hidden="true" className="absolute bottom-0 left-[2px] top-0 w-[3px] origin-top rounded-full bg-accent shadow-[0_0_12px_#4FB0E7] lg:hidden" style={{ transform: 'scaleY(1)' }} />
        <ol className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS.map((item, i) => (
            <li
              key={item.step}
              ref={(el) => (stepsRef.current[i] = el)}
              className="group bg-base p-7 transition-[background-color,opacity,transform] duration-500 max-lg:translate-x-2 max-lg:opacity-50 [&.is-on]:bg-raised max-lg:[&.is-on]:translate-x-0 max-lg:[&.is-on]:opacity-100"
            >
              <span className="tabular display-semi text-3xl text-muted/50 transition-colors duration-500 group-[.is-on]:text-accent">
                {item.step}
              </span>
              <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
              <p className="mt-3 text-[0.9375rem] text-muted">{item.description}</p>
            </li>
          ))}
        </ol>
        </div>
      </div>
    </section>
  );
}
